// Catalog of existing Identity Registries available for reuse at CF deploy time.
// Per spec §11.2: IRs are bound to a CF at deployment, and may be shared across
// CFs with identical claim policy. The CTR (claim topics) and TIR (trusted issuers
// per topic) are bound to the IR — once an admin reuses an existing IR, those
// fields are read-only from the wizard's perspective.

export interface RegistryClaimTopic {
  label: string;
  required: boolean;
  ascendSpecific?: boolean;
}

export interface RegistryPolicy {
  id: string;
  name: string;
  jurisdiction: string;            // ISO code or 'GLOBAL'
  trustedIssuer: string;           // operator address (Ascend MPC signer in this setup)
  sharedWith: number;              // count of other CFs already bound to this IR
  claimTopics: RegistryClaimTopic[];
  description: string;
}

export const EXISTING_REGISTRIES: RegistryPolicy[] = [
  {
    id: 'ascend-default',
    name: 'Ascend Default Registry',
    jurisdiction: 'US',
    trustedIssuer: '0xA5cE1d000000000000000000000000000000ABcD',
    sharedWith: 4,
    description:
      'Standard institutional gating: KYC + accreditation. Used by most US-jurisdiction credit facilities.',
    claimTopics: [
      { label: 'KYC verified', required: true },
      { label: 'Accredited Investor', required: true },
      { label: 'Partner Membership', required: true, ascendSpecific: true },
      { label: 'Corporate Email Verified', required: true, ascendSpecific: true },
    ],
  },
  {
    id: 'ascend-eu-qualified',
    name: 'Ascend EU Qualified Registry',
    jurisdiction: 'EU',
    trustedIssuer: '0xA5cE1d000000000000000000000000000000ABcD',
    sharedWith: 1,
    description:
      'EU jurisdiction with MiFID II Qualified Investor framing. Tightly scoped trusted-issuer set.',
    claimTopics: [
      { label: 'KYC verified', required: true },
      { label: 'Qualified Purchaser', required: true },
      { label: 'Jurisdiction: EU', required: true },
    ],
  },
  {
    id: 'ascend-institutional-only',
    name: 'Ascend Institutional-Only Registry',
    jurisdiction: 'GLOBAL',
    trustedIssuer: '0xA5cE1d000000000000000000000000000000ABcD',
    sharedWith: 0,
    description:
      'Institutional investors only — no individual accreditation. Used for credit facilities with very large minimums.',
    claimTopics: [
      { label: 'KYC verified', required: true },
      { label: 'Institutional Investor', required: true },
      { label: 'Qualified Premium Investor', required: false, ascendSpecific: true },
    ],
  },
];

export function getRegistry(id: string): RegistryPolicy | undefined {
  return EXISTING_REGISTRIES.find((r) => r.id === id);
}
